import React, { useMemo } from 'react';

/** Smooth cubic path through sparkline points. */
const buildSmoothLine = (coords) => {
  if (!coords.length) return '';
  if (coords.length === 1) return `M${coords[0][0]},${coords[0][1]}`;
  let d = `M${coords[0][0].toFixed(2)},${coords[0][1].toFixed(2)}`;
  for (let i = 0; i < coords.length - 1; i += 1) {
    const [x0, y0] = coords[i];
    const [x1, y1] = coords[i + 1];
    const cx = (x0 + x1) / 2;
    d += ` C${cx.toFixed(2)},${y0.toFixed(2)} ${cx.toFixed(2)},${y1.toFixed(2)} ${x1.toFixed(2)},${y1.toFixed(2)}`;
  }
  return d;
};

const normalizeSparkNums = (values) => {
  let nums = (values || []).map(Number).filter((v) => Number.isFinite(v));
  if (nums.length === 1) nums = [nums[0], nums[0]];
  return nums;
};

const parseHex = (hex) => {
  const raw = String(hex || '').replace('#', '').trim();
  if (raw.length !== 3 && raw.length !== 6) return { r: 45, g: 140, b: 255 };
  const full = raw.length === 3
    ? raw.split('').map((c) => `${c}${c}`).join('')
    : raw;
  const n = parseInt(full, 16);
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  };
};

/** Soft pastel wash (accent blended into white) — like HTML --wash. */
const washFromAccent = (color) => {
  const { r, g, b } = parseHex(color);
  const mix = 0.14;
  const wr = Math.round(255 * (1 - mix) + r * mix);
  const wg = Math.round(255 * (1 - mix) + g * mix);
  const wb = Math.round(255 * (1 - mix) + b * mix);
  return `rgb(${wr}, ${wg}, ${wb})`;
};

const BACKGROUND_WAVES = [
  'M0 175 C95 125 165 155 215 92 C250 48 265 25 320 18 L320 240 L0 240 Z',
  'M0 190 C90 160 145 165 195 105 C235 57 255 35 320 25 L320 240 L0 240 Z',
  'M0 205 C75 180 150 160 205 108 C250 65 275 35 320 27 L320 240 L0 240 Z',
];

const buildSparkGeometry = (nums) => {
  const width = 320;
  const height = 55;
  const padX = 12;
  const padY = 8;
  if (nums.length < 2) return null;

  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || 1;
  const coords = nums.map((v, i) => {
    const x = padX + (i * (width - padX * 2)) / Math.max(nums.length - 1, 1);
    const y = height - padY - ((v - min) / span) * (height - padY * 2);
    return [x, y];
  });
  const line = buildSmoothLine(coords);
  const last = coords[coords.length - 1];
  const first = coords[0];
  const area = `${line} L${last[0].toFixed(2)},${height} L${first[0].toFixed(2)},${height} Z`;
  return { line, area, last, width, height };
};

const DeltaBadge = ({ value, label }) => {
  if (value == null || !Number.isFinite(value)) return null;
  const up = value >= 0;
  return (
    <div
      className={`w-[4.9rem] rounded-[9px] border border-white/75 px-[7px] py-1.5 ${
        up ? 'bg-emerald-50/75' : 'bg-rose-50/75'
      }`}
    >
      <strong
        className={`block whitespace-nowrap text-[11px] font-bold leading-tight ${
          up ? 'text-emerald-500' : 'text-rose-500'
        }`}
      >
        {up ? '↑' : '↓'} {Math.abs(value).toFixed(1)}%
      </strong>
      <span className="mt-1 block whitespace-nowrap text-[8.5px] font-semibold text-slate-500">
        {label}
      </span>
    </div>
  );
};

/**
 * KPI card matching the provided HTML reference:
 * wash wave background, left white fade, corner accent bar, sparkline.
 */
const BiKpiCard = ({
  icon: Icon,
  label,
  value,
  color = '#2d8cff',
  sparkValues,
  deltas = {},
  sub = null,
  waveVariant = 0,
}) => {
  const vsPrev = deltas.vsPrev ?? deltas.vsPrevPeriod ?? deltas.vsPrevWeek;
  const vsAvg = deltas.vsAvg ?? deltas.vsAvgPrev4;
  const wash = washFromAccent(color);
  const wavePath = BACKGROUND_WAVES[Math.abs(waveVariant) % BACKGROUND_WAVES.length];

  const spark = useMemo(() => {
    const nums = normalizeSparkNums(sparkValues);
    return buildSparkGeometry(nums.length >= 2 ? nums : [0, 0]);
  }, [sparkValues]);

  return (
    <article
      className="relative isolate h-[190px] min-w-0 overflow-hidden rounded-[14px] border border-[#e7ebf1] bg-white px-3.5 pb-2.5 pt-4 shadow-[0_2px_5px_rgba(15,23,42,0.025),0_8px_20px_rgba(15,23,42,0.045)]"
      style={{ '--accent': color, '--wash': wash }}
    >
      <svg
        className="pointer-events-none absolute inset-0 -z-[2] h-full w-full"
        viewBox="0 0 320 240"
        preserveAspectRatio="none"
        aria-hidden
      >
        <path
          d={wavePath}
          fill="var(--wash)"
          opacity="0.72"
          style={{ filter: 'blur(1px)' }}
        />
      </svg>

      <div
        className="pointer-events-none absolute inset-0 -z-[1]"
        style={{
          background:
            'linear-gradient(90deg, rgba(255,255,255,0.99), rgba(255,255,255,0.94) 34%, rgba(255,255,255,0.62) 64%, rgba(255,255,255,0.15))',
        }}
        aria-hidden
      />

      <span
        className="absolute left-0 top-0 z-[3] h-1 w-[13px] rounded-br-lg"
        style={{ background: 'var(--accent)' }}
        aria-hidden
      />

      <div className="relative z-[2] flex items-center gap-2.5">
        <div
          className="grid h-[42px] w-[42px] shrink-0 place-items-center rounded-xl border border-white/90 shadow-[0_4px_10px_rgba(15,23,42,0.055),inset_0_1px_rgba(255,255,255,0.95)]"
          style={{
            background: 'linear-gradient(145deg, rgba(255,255,255,0.95), var(--wash))',
            color: 'var(--accent)',
          }}
        >
          {Icon ? <Icon className="h-[18px] w-[18px]" strokeWidth={2.25} /> : null}
        </div>
        <span className="min-w-0 truncate text-[13px] font-semibold text-slate-600">
          {label}
        </span>
      </div>

      <div className="relative z-[2] mt-2 text-[30px] font-bold leading-none tracking-tight text-[#102447]">
        {value}
      </div>

      <div className="relative z-[2] mt-2.5 flex gap-2">
        <DeltaBadge value={vsPrev} label="vs previous" />
        <DeltaBadge value={vsAvg} label="vs avg" />
      </div>

      {sub ? (
        <p className="relative z-[2] mt-1.5 text-[10px] font-medium text-slate-500">{sub}</p>
      ) : null}

      {spark ? (
        <div className="absolute bottom-[9px] left-3.5 right-3.5 z-[2] h-[35px]">
          <svg
            viewBox={`0 0 ${spark.width} ${spark.height}`}
            className="h-full w-full overflow-visible"
            preserveAspectRatio="none"
            aria-hidden
          >
            <path d={spark.area} fill="var(--wash)" opacity="0.48" />
            <path
              d={spark.line}
              fill="none"
              stroke="var(--accent)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
            <circle
              cx={spark.last[0]}
              cy={spark.last[1]}
              r="5"
              fill="#fff"
              stroke="var(--accent)"
              strokeWidth="2.5"
            />
          </svg>
        </div>
      ) : null}
    </article>
  );
};

export default BiKpiCard;
